import { createStore, useStore } from '@w/react-store'
import { createContext, use, type ReactNode } from 'react'
import { storeOf, type RealtimeClient, type RealtimeStatus, type ReceivedLiveEdit } from './client'
import type { Peer } from './protocol'

const RealtimeContext = createContext<RealtimeClient | null>(null)

/** `client: null` (signed out, non-admin): every hook reports no collaborators and status `closed`. */
export function RealtimeProvider({ client, children }: { client: RealtimeClient | null; children: ReactNode }) {
  return <RealtimeContext value={client}>{children}</RealtimeContext>
}

export function useRealtime(): RealtimeClient | null {
  return use(RealtimeContext)
}

const NO_CLIENT = createStore<{ status: RealtimeStatus; peers: Peer[]; edits: readonly ReceivedLiveEdit[] }>({ status: 'closed', peers: [], edits: [] })

/** The other connections currently on `pageId` (several per user when they have several tabs open). */
export function useCollaborators(pageId: string, client: RealtimeClient | null = useRealtime()): Peer[] {
  const peers = useStore((client ? storeOf(client) : NO_CLIENT).select('peers'))
  return peers.filter((peer) => peer.state.pageId === pageId)
}

export function useRealtimeStatus(client: RealtimeClient | null = useRealtime()): RealtimeStatus {
  return useStore((client ? storeOf(client) : NO_CLIENT).select('status'))
}

/** Frames awaiting application by the current page. Consuming these does not establish durable coverage. */
export function useLiveEdits(client: RealtimeClient | null = useRealtime()): readonly ReceivedLiveEdit[] {
  return useStore((client ? storeOf(client) : NO_CLIENT).select('edits'))
}
